/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */


var cartDrawer = document.getElementById('mini-cart');
var headerSection = document.querySelector('.shopify-section--header');

function isLegacyAndroidChrome() {
  var userAgent = window.navigator.userAgent || '';
  var androidMatch = userAgent.match(/Android\s([0-9.]+)/i);
  var chromeMatch = userAgent.match(/Chrome\/(\d+)/i);

  if (!androidMatch || !chromeMatch) {
    return false;
  }

  var chromeVersion = parseInt(chromeMatch[1], 10);

  if (isNaN(chromeVersion)) {
    return false;
  }

  return chromeVersion < 90;
}

function setCollapsibleState(toggle, expanded) {
  if (!toggle) {
    return;
  }

  var controlledId = toggle.getAttribute('aria-controls');
  var controlledElement = controlledId ? document.getElementById(controlledId) : null;

  toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');

  if (!controlledElement) {
    return;
  }

  if (expanded) {
    controlledElement.setAttribute('open', '');
    controlledElement.removeAttribute('hidden');
  } else {
    controlledElement.removeAttribute('open');
    controlledElement.setAttribute('hidden', '');
  }
}

function initLegacyMobileMenuFallback() {
  if (!isLegacyAndroidChrome()) {
    return;
  }

  document.documentElement.classList.add('legacy-browser');

  var mobileMenuDrawer = document.getElementById('mobile-menu-drawer');
  var menuToggle = document.querySelector('button[aria-controls="mobile-menu-drawer"]');

  if (!mobileMenuDrawer || !menuToggle) {
    return;
  }

  var closeButton = mobileMenuDrawer.querySelector('[data-action="close"]');
  var overlay = mobileMenuDrawer.querySelector('.drawer__overlay');
  var submenuToggles = Array.prototype.slice.call(mobileMenuDrawer.querySelectorAll('.mobile-nav__link[aria-controls]'));

  function openMenu() {
    mobileMenuDrawer.setAttribute('open', '');
    menuToggle.setAttribute('aria-expanded', 'true');
    document.documentElement.classList.add('legacy-mobile-menu-open');
    document.body.classList.add('legacy-mobile-menu-open');
  }

  function closeMenu() {
    mobileMenuDrawer.removeAttribute('open');
    menuToggle.setAttribute('aria-expanded', 'false');
    document.documentElement.classList.remove('legacy-mobile-menu-open');
    document.body.classList.remove('legacy-mobile-menu-open');
  }

  menuToggle.addEventListener('click', function(event) {
    event.preventDefault();
    event.stopImmediatePropagation();

    if (mobileMenuDrawer.hasAttribute('open')) {
      closeMenu();
    } else {
      openMenu();
    }
  }, true);

  if (closeButton) {
    closeButton.addEventListener('click', function(event) {
      event.preventDefault();
      closeMenu();
    }, true);
  }

  if (overlay) {
    overlay.addEventListener('click', function(event) {
      event.preventDefault();
      closeMenu();
    }, true);
  }

  submenuToggles.forEach(function(toggle) {
    setCollapsibleState(toggle, false);

    toggle.addEventListener('click', function(event) {
      event.preventDefault();
      event.stopImmediatePropagation();
      var isExpanded = toggle.getAttribute('aria-expanded') === 'true';
      setCollapsibleState(toggle, !isExpanded);
    }, true);
  });

  document.addEventListener('keyup', function(event) {
    if (event.key === 'Escape' || event.keyCode === 27) {
      closeMenu();
    }
  });

  Array.prototype.slice.call(mobileMenuDrawer.querySelectorAll('a.mobile-nav__link')).forEach(function(link) {
    link.addEventListener('click', function(event) {
      event.stopPropagation();
    }, true);
  });
}

function initLegacyProductFaqFallback() {
  if (!isLegacyAndroidChrome()) {
    return;
  }

  var faqToggles = Array.prototype.slice.call(document.querySelectorAll('body.template-product .shopify-section--faq .collapsible-toggle[aria-controls]'));

  if (!faqToggles.length) {
    return;
  }

  faqToggles.forEach(function(toggle) {
    var controlledId = toggle.getAttribute('aria-controls');
    var content = controlledId ? document.getElementById(controlledId) : null;

    if (!content) {
      return;
    }

    setCollapsibleState(toggle, content.hasAttribute('open'));

    toggle.addEventListener('click', function(event) {
      event.preventDefault();
      event.stopImmediatePropagation();

      var isExpanded = toggle.getAttribute('aria-expanded') === 'true';
      setCollapsibleState(toggle, !isExpanded);
      toggle.closest('.faq__item') && toggle.closest('.faq__item').classList.toggle('selected', !isExpanded);
    }, true);
  });
}

function updateHeaderZIndexForCart() {
  if (!cartDrawer || !headerSection) {
    return;
  }

  if (cartDrawer.hasAttribute('open')) {
    headerSection.style.zIndex = '0';
  } else {
    headerSection.style.zIndex = '';
  }
}

updateHeaderZIndexForCart();
initLegacyMobileMenuFallback();
initLegacyProductFaqFallback();

if (cartDrawer && headerSection && 'MutationObserver' in window) {
  var observer = new MutationObserver(function(mutations) {
    mutations.forEach(function(mutation) {
      if (mutation.attributeName === 'open') {
        updateHeaderZIndexForCart();
      }
    });
  });

  observer.observe(cartDrawer, { attributes: true, attributeFilter: ['open'] });
}



document.addEventListener('DOMContentLoaded', function() {
  var tabItem = document.querySelector('.faq__item.product-form__tabs');
  var parentElement = tabItem ? tabItem.parentNode : null;
  var firstFaqItem = parentElement ? parentElement.querySelector('.faq__item.product-form__tabs') : null;

  if (firstFaqItem) {
    firstFaqItem.classList.add('selected');
  }

  const accList = document.querySelectorAll(".product-form .faq__item")
  function clearAll(){
      accList.forEach(e =>{
          e.classList.remove("faqActive")
          var toggle = e.querySelector('.pro-toggle');
          if (toggle) {
            toggle.setAttribute('aria-expanded', 'false');
          }
      })
  }
  accList.forEach((e) => {
      var toggle = e.querySelector('.pro-toggle');

      if (!toggle) {
        return;
      }

      toggle.addEventListener("click", function(event) {
        event.preventDefault();
        event.stopPropagation();

        if( e.classList.contains("faqActive")){
          e.classList.remove("faqActive")
          toggle.setAttribute('aria-expanded', 'false');
        }else{
          clearAll()
          e.classList.add("faqActive")
          toggle.setAttribute('aria-expanded', 'true');
        }
  
      })
  })
});

document.addEventListener('DOMContentLoaded', function() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return;
  }

  const revealTargets = Array.from(document.querySelectorAll('body.template-product .shopify-section, body.template-product .m-testimonials'));

  if (!revealTargets.length || !('IntersectionObserver' in window)) {
    return;
  }

  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

  const observer = new IntersectionObserver(function(entries) {
    entries.forEach(function(entry) {
      if (!entry.isIntersecting) {
        return;
      }

      entry.target.classList.add('is-visible');
      observer.unobserve(entry.target);
    });
  }, {
    threshold: 0.18,
    rootMargin: '0px 0px -8% 0px'
  });

  revealTargets.forEach(function(section) {
    if (section.classList.contains('shopify-section--main-product')) {
      return;
    }

    if (section.closest('.shopify-section-group-footer-group')) {
      return;
    }

    const rect = section.getBoundingClientRect();

    if (rect.top >= 0 && rect.top < viewportHeight && rect.bottom > 0) {
      return;
    }

    section.classList.add('product-page-reveal');
    observer.observe(section);
  });
});

/* Mini-cart discount code field.
   The cart drawer is fully re-rendered (innerHTML swap) after every cart
   change, so all handlers use document-level delegation and the typed code
   is kept in sessionStorage to survive re-renders. */
(function () {
  var STORAGE_KEY = 'mc_discount_code';

  function getInput() {
    return document.getElementById('mini-cart-discount-code');
  }

  function applyCode() {
    var input = getInput();
    if (!input) return;

    var code = input.value.trim();
    if (!code) {
      input.focus();
      return;
    }

    sessionStorage.setItem(STORAGE_KEY, code);
    window.location.href = '/discount/' + encodeURIComponent(code) + '?redirect=/checkout';
  }

  document.addEventListener('click', function (event) {
    if (event.target.closest('#mini-cart-discount-apply')) {
      applyCode();
    }
  });

  document.addEventListener('input', function (event) {
    if (event.target.id === 'mini-cart-discount-code') {
      sessionStorage.setItem(STORAGE_KEY, event.target.value);
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.target.id === 'mini-cart-discount-code' && event.key === 'Enter') {
      event.preventDefault();
      applyCode();
    }
  });

  // Restore the typed code after the drawer re-renders.
  var drawer = document.querySelector('cart-drawer');
  if (drawer) {
    new MutationObserver(function () {
      var input = getInput();
      var saved = sessionStorage.getItem(STORAGE_KEY);
      if (input && saved && !input.value) {
        input.value = saved;
      }
    }).observe(drawer, { childList: true, subtree: true });
  }
})();

/* Gold toast notification — call window.mcToast('message') from anywhere. */
(function () {
  var toastEl = null;
  var hideTimer = null;

  window.mcToast = function (message) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'mc-toast';
      toastEl.setAttribute('role', 'status');
      document.body.appendChild(toastEl);
    }

    toastEl.textContent = message;
    toastEl.classList.add('mc-toast--visible');
    clearTimeout(hideTimer);
    hideTimer = setTimeout(function () {
      toastEl.classList.remove('mc-toast--visible');
    }, 2600);
  };
})();

/* Coupon-to-cart bridge: copying a code on a product/landing page stashes it
   so the cart-drawer discount field is pre-filled automatically. */
(function () {
  var STORAGE_KEY = 'mc_discount_code';

  window.mcSetDiscountCode = function (code) {
    try {
      sessionStorage.setItem(STORAGE_KEY, code);
    } catch (e) {}
  };

  function prefill() {
    var saved = null;
    try {
      saved = sessionStorage.getItem(STORAGE_KEY);
    } catch (e) {}

    var input = document.getElementById('mini-cart-discount-code');
    if (input && saved && !input.value) {
      input.value = saved;
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', prefill);
  } else {
    prefill();
  }
})();
